import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Download, FlaskConical, Loader2, Lock, Minus, Plus, RotateCcw, Search, Upload, Wand2, Trash2, History, X } from "lucide-react";
import { api, downloadFile, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import { ORG_BADGE, ORG_LABEL, STATI_ESECUZIONE, STATO_BADGE, fmtDate, fmtDateTime } from "@/lib/constants";
import { AIUTI } from "@/lib/guide";
import { Hint } from "@/components/Guida";
import { PmBot } from "@/components/PmBot";

const KoDetail = ({ task, onRiprendi, canRiprendi }) => {
  const [aperto, setAperto] = useState(false);
  const [busy, setBusy] = useState(false);

  const scarica = async () => {
    try {
      await downloadFile(`/documents/${task.allegato_id}/download`, task.allegato_nome || "allegato");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const riprendi = async () => {
    setBusy(true);
    try {
      await api.post(`/tasks/${task.id}/riprendi`);
      toast.success(`${task.codice} ripreso in lavorazione`);
      setAperto(false);
      onRiprendi();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  return (
    <>
      <button onClick={() => setAperto(true)} data-testid={`ko-detail-button-${task.codice}`}
              className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-rose-300 bg-rose-50 px-2 py-1.5 text-[11px] font-semibold text-rose-700 transition-colors hover:bg-rose-100">
        <AlertTriangle className="h-3 w-3" /> KO dal tester · dettaglio
      </button>

      {aperto && (
        <div className="fixed inset-0 z-[9997] flex items-end justify-center bg-slate-900/50 p-4 sm:items-center"
             onClick={() => setAperto(false)}>
          <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl"
               onClick={(e) => e.stopPropagation()} data-testid={`ko-detail-modal-${task.codice}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-rose-600">
                  Segnalazione del tester
                </p>
                <h3 className="mt-1 text-lg font-semibold leading-snug text-slate-900">
                  {task.codice} · {task.task}
                </h3>
              </div>
              <button onClick={() => setAperto(false)} data-testid={`ko-detail-close-${task.codice}`}
                      className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-900">
                <X className="h-4 w-4" />
              </button>
            </div>

            <p className="mt-3 font-mono text-[11px] text-slate-500">
              {task.testato_da || "Tester"} · {fmtDateTime(task.testato_il)} · rework {task.rework_count || 0}
            </p>

            <div className="mt-3 rounded-xl bg-rose-50 p-3" data-testid={`ko-detail-note-${task.codice}`}>
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-rose-600">Bug segnalato</p>
              <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                {task.note_test || "Nessuna nota inserita."}
              </p>
            </div>

            {task.allegato_id ? (
              <Button onClick={scarica} variant="outline" className="mt-3 h-10 w-full"
                      data-testid={`ko-detail-download-${task.codice}`}>
                <Download className="mr-2 h-4 w-4" /> Scarica {task.allegato_nome || "allegato"}
              </Button>
            ) : (
              <p className="mt-3 text-xs text-slate-400">Nessun allegato disponibile.</p>
            )}

            {canRiprendi && (
              <Button onClick={riprendi} disabled={busy}
                      className="mt-2 h-10 w-full bg-slate-900 text-white hover:bg-slate-800"
                      data-testid={`ko-detail-riprendi-${task.codice}`}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                Riprendi in lavorazione
              </Button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const ReworkStorico = ({ task }) => {
  const voci = task.rework_storico || [];
  if (!voci.length) return null;
  const perTester = voci.reduce((acc, v) => {
    acc[v.tester || "Tester"] = (acc[v.tester || "Tester"] || 0) + 1;
    return acc;
  }, {});
  return (
    <div className="mt-2 rounded-md border border-rose-200 bg-rose-50/70 px-2 py-1.5"
         data-testid={`rework-storico-${task.codice}`}>
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-rose-600">
        KO dal tester: {voci.length} {voci.length === 1 ? "respingimento" : "respingimenti"}
      </p>
      <p className="mt-0.5 text-[11px] leading-relaxed text-slate-600">
        {Object.entries(perTester).map(([tester, n]) => `${tester} (${n})`).join(" · ")}
      </p>
      <p className="mt-0.5 font-mono text-[10px] text-slate-400">
        Ultimo KO: {fmtDateTime(voci[voci.length - 1].ts)}
      </p>
    </div>
  );
};

const TaskHistory = ({ task }) => {
  const [aperto, setAperto] = useState(false);
  const [voci, setVoci] = useState(null);

  const toggle = async () => {
    const nuovo = !aperto;
    setAperto(nuovo);
    if (nuovo && voci === null) {
      try {
        const { data } = await api.get(`/tasks/${task.id}/audit`);
        setVoci(data);
      } catch {
        setVoci([]);
      }
    }
  };

  return (
    <div className="mt-2">
      <button
        onClick={toggle}
        data-testid={`task-history-toggle-${task.codice}`}
        className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.12em] text-slate-400 transition-colors hover:text-slate-900"
      >
        <History className="h-3 w-3" /> {aperto ? "Chiudi cronologia" : "Cronologia modifiche"}
      </button>
      {aperto && (
        <div className="mt-2 space-y-1.5 rounded-lg border border-slate-100 bg-slate-50/70 p-2.5"
             data-testid={`task-history-${task.codice}`}>
          {voci === null && <p className="text-[11px] text-slate-400">Caricamento…</p>}
          {voci?.length === 0 && <p className="text-[11px] text-slate-400">Nessuna modifica registrata.</p>}
          {voci?.map((v) => (
            <p key={v.id} className="text-[11px] leading-relaxed text-slate-600">
              <span className="font-mono text-slate-400">{fmtDateTime(v.ts)}</span>{" "}
              <span className="font-semibold text-slate-900">{v.utente}</span>{" "}
              {v.campo_label}: <span className="text-slate-400 line-through">{String(v.da ?? "vuoto")}</span>{" "}
              → <span className="font-semibold text-emerald-700">{String(v.a ?? "vuoto")}</span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

/** scope: "all" (admin), "cliente" (dev/pm), "fornitore" */
export default function Piano({ scope = "all", titolo, sottotitolo, readOnly = false }) {
  const { user } = useAuth();
  const { projectId } = useProject();
  const [plan, setPlan] = useState(null);
  const [sprint, setSprint] = useState("all");
  const [assegnatario, setAssegnatario] = useState("all");
  const [stato, setStato] = useState("all");
  const [org, setOrg] = useState("all");
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState("");
  const [importing, setImporting] = useState(false);

  const load = () =>
    api
      .get(`/projects/${projectId}/plan`)
      .then(({ data }) => setPlan(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));

  useEffect(() => {
    if (!projectId) return;
    setPlan(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const role = user.role;
  // Sequenzialità sprint: nessuno lavora sullo sprint N se il precedente non è chiuso al 100%.
  const bloccatoDaSprint = (t) => (role === "admin" ? null : t.bloccato_da || null);
  // Dev e fornitore si fermano a "In Test": dopo la consegna al test il task non è più loro.
  const bloccatoDalTest = (t) => ["In Test", "OK"].includes(t.stato);
  // le attività di test sono del tester: dev e fornitore non le aggiornano
  const attivitaDiTest = (t) => t.tipo === "Test" && role !== "admin";
  const canEditFornitore = (t) =>
    !readOnly && !bloccatoDaSprint(t) && !attivitaDiTest(t) && t.org === "fornitore" &&
    (role === "admin" || (role === "fornitore" && !bloccatoDalTest(t)));
  const canEditCliente = (t) =>
    !readOnly && !bloccatoDaSprint(t) && !attivitaDiTest(t) &&
    (role === "admin" || (role === "dev" && !bloccatoDalTest(t)));
  const canEditCommon = (t) =>
    !readOnly && !bloccatoDaSprint(t) && !attivitaDiTest(t) &&
    (role === "admin" ||
      (role === "dev" && !bloccatoDalTest(t)) ||
      (role === "fornitore" && t.org === "fornitore" && !bloccatoDalTest(t)));
  const statiSelezionabili = (t) =>
    role === "admin"
      ? plan.stati
      : [...new Set([t.stato, ...STATI_ESECUZIONE])];

  const save = async (task, patch) => {
    setSaving(task.id);
    try {
      const { data } = await api.patch(`/tasks/${task.id}`, patch);
      setPlan((p) => ({ ...p, tasks: p.tasks.map((t) => (t.id === data.id ? { ...t, ...data } : t)) }));
      toast.success(`${task.codice} aggiornato`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      load();
    }
    setSaving("");
  };

  const allinea = async (task) => {
    setSaving(task.id);
    try {
      const { data } = await api.post(`/tasks/${task.id}/allinea`);
      setPlan((p) => ({ ...p, tasks: p.tasks.map((t) => (t.id === data.id ? { ...t, ...data } : t)) }));
      toast.success(`${task.codice}: consolidato al ${data.percentuale_cliente}% di Stefano Critelli`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving("");
  };

  const removeTask = async (task) => {
    try {
      await api.delete(`/tasks/${task.id}`);
      setPlan((p) => ({ ...p, tasks: p.tasks.filter((t) => t.id !== task.id) }));
      toast.success(`${task.codice} eliminato`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const importExcel = async (file) => {
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/projects/${projectId}/import-excel`, fd);
      toast.success(`Import completato: ${data.aggiornati} aggiornati, ${data.creati} creati`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setImporting(false);
  };

  const scopedTasks = useMemo(() => {
    if (!plan) return [];
    const base = scope === "fornitore" ? plan.tasks.filter((t) => t.org === "fornitore") : plan.tasks;
    return base.filter(
      (t) =>
        (sprint === "all" || t.sprint === sprint) &&
        (assegnatario === "all" || t.assegnatario === assegnatario) &&
        (stato === "all" || t.stato === stato) &&
        (org === "all" || t.org === org) &&
        (!q || `${t.codice} ${t.task}`.toLowerCase().includes(q.toLowerCase()))
    );
  }, [plan, scope, sprint, assegnatario, stato, org, q]);

  if (!plan)
    return (
      <div className="h-64 grid place-items-center text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );

  const canImport = !readOnly && (role === "admin" || role === "dev");

  return (
    <div className="space-y-6" data-testid={`piano-page-${scope}`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">{plan.project.nome}</p>
          <h1 className="mt-1 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{titolo}</h1>
          <p className="mt-2 text-sm text-slate-600 max-w-3xl">{sottotitolo}</p>
        </div>
        {canImport && (
          <label className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-300 bg-white text-sm font-medium cursor-pointer hover:bg-slate-900 hover:text-white transition-colors"
                 data-testid="import-excel-label">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Importa Excel
            <Hint id="import-excel" testo={AIUTI.importExcel} />
            <input type="file" accept=".xlsx,.xls" className="hidden" data-testid="import-excel-input"
                   onChange={(e) => importExcel(e.target.files[0])} />
          </label>
        )}
      </div>




      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-wrap gap-3 items-center"
           data-testid="filter-bar">
        <div className="relative flex-1 min-w-[190px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca codice o task..."
                 className="pl-9 h-10" data-testid="task-search-input" />
        </div>
        <Select value={sprint} onValueChange={setSprint}>
          <SelectTrigger className="w-[180px] h-10" data-testid="filter-sprint-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Tutti gli sprint</SelectItem>
            {plan.sprints.map((s) => (<SelectItem key={s.key} value={s.key}>{s.nome}</SelectItem>))}
          </SelectContent>
        </Select>
        {scope !== "fornitore" && (
          <Select value={org} onValueChange={setOrg}>
            <SelectTrigger className="w-[150px] h-10" data-testid="filter-org-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Tutti gli esecutori</SelectItem>
              <SelectItem value="fornitore">Stefano Critelli</SelectItem>
              <SelectItem value="cliente">Majorbit</SelectItem>
            </SelectContent>
          </Select>
        )}
        <Select value={assegnatario} onValueChange={setAssegnatario}>
          <SelectTrigger className="w-[170px] h-10" data-testid="filter-assignee-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Tutti gli assegnatari</SelectItem>
            {plan.assegnatari.map((a) => (<SelectItem key={a} value={a}>{a}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={stato} onValueChange={setStato}>
          <SelectTrigger className="w-[150px] h-10" data-testid="filter-status-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {plan.stati.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
          </SelectContent>
        </Select>
        <span className="font-mono text-xs text-slate-500" data-testid="task-count">{scopedTasks.length} task</span>
      </div>

      <div className="space-y-3">
        {scopedTasks.map((t) => {
          const editBc = canEditFornitore(t);
          const editMj = canEditCliente(t);
          const editCommon = canEditCommon(t);
          const disallineato = t.org === "fornitore" && t.percentuale_fornitore !== t.percentuale_cliente;
          return (
            <div key={t.id}
                 className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:border-slate-300 transition-colors"
                 data-testid={`task-row-${t.codice}`}>
              <div className="grid lg:grid-cols-12 gap-5">
                <div className="lg:col-span-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs font-semibold px-2 py-0.5 rounded bg-slate-900 text-white">
                      {t.codice}
                    </span>
                    <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full border ${ORG_BADGE[t.org]}`}
                          data-testid={`task-org-${t.codice}`}>
                      {ORG_LABEL[t.org]}
                    </span>
                    <span className="font-mono text-[10px] px-2 py-0.5 rounded-full border border-slate-200 text-slate-600">
                      {t.area} · {t.tipo}
                    </span>
                    <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full border ${STATO_BADGE[t.stato]}`}>
                      {t.stato}
                    </span>
                    {!editCommon && <Lock className="h-3.5 w-3.5 text-slate-300" />}
                  </div>
                  {bloccatoDaSprint(t) && (
                    <p className="mt-2 flex items-center gap-1.5 rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] text-rose-700"
                       data-testid={`task-sprint-locked-${t.codice}`}>
                      <Lock className="h-3 w-3" /> {bloccatoDaSprint(t)} non è ancora tutto in test: sprint bloccato
                    </p>
                  )}
                  {bloccatoDalTest(t) && role !== "admin" && role !== "pm" && !bloccatoDaSprint(t) && (
                    <p className="mt-2 flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-700"
                       data-testid={`task-locked-${t.codice}`}>
                      <Lock className="h-3 w-3" /> Consegnato al test: l'esito è del tester
                    </p>
                  )}
                  <p className="mt-2 font-medium text-slate-900 leading-snug">{t.task}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    {t.assegnatario || "Non assegnato"} · {fmtDate(t.data_inizio)} → {fmtDate(t.data_fine)} · {t.stima} gg
                  </p>
                  {t.updated_by && (
                    <p className="mt-1 text-[11px] text-slate-400 font-mono">
                      Ultimo agg.: {t.updated_by}
                      {t.updated_at ? ` · ${new Date(t.updated_at).toLocaleString("it-IT")}` : ""}
                    </p>
                  )}
                  {attivitaDiTest(t) && (
                    <p className="mt-2 flex items-center gap-1.5 rounded-md border border-violet-200 bg-violet-50 px-2 py-1 text-[11px] text-violet-700"
                       data-testid={`task-test-owned-${t.codice}`}>
                      <FlaskConical className="h-3 w-3" /> Attività di test: avanzamento e stato li aggiorna il tester
                    </p>
                  )}
                  <TaskHistory task={t} />
                  <ReworkStorico task={t} />
                  {t.esito_test === "KO" && role !== "cliente_finale" && (
                    <KoDetail
                      task={t}
                      onRiprendi={load}
                      canRiprendi={
                        !readOnly && !bloccatoDaSprint(t) &&
                        (role === "admin" || role === "dev" ||
                          (role === "fornitore" && t.org === "fornitore"))
                      }
                    />
                  )}
                  {role === "admin" && (
                    <div className="mt-3 flex gap-2">
                      <Select value={t.org} onValueChange={(v) => save(t, { org: v })}>
                        <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`task-org-select-${t.codice}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="fornitore">Stefano Critelli</SelectItem>
                          <SelectItem value="cliente">Majorbit</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="sm" onClick={() => removeTask(t)}
                              className="h-8 text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                              data-testid={`task-delete-${t.codice}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>

                <div className="lg:col-span-5 space-y-4">
                  {t.org === "fornitore" && (
                    <div>
                      <div className="flex justify-between items-center mb-1.5">
                        <label className="text-xs font-semibold uppercase tracking-wider text-indigo-500 font-mono">
                          % Stefano Critelli
                          <Hint id="perc-fornitore" testo={AIUTI.percFornitore} />
                        </label>
                        <span className="font-mono text-sm font-bold text-indigo-700"
                              data-testid={`task-fornitore-value-${t.codice}`}>
                          {t.percentuale_fornitore}%
                        </span>
                      </div>
                      <Slider value={[t.percentuale_fornitore]} min={0} max={100} step={5} disabled={!editBc}
                              onValueChange={([v]) =>
                                setPlan((p) => ({
                                  ...p,
                                  tasks: p.tasks.map((x) => (x.id === t.id ? { ...x, percentuale_fornitore: v } : x)),
                                }))
                              }
                              onValueCommit={([v]) => save(t, { percentuale_fornitore: v })}
                              data-testid={`task-fornitore-slider-${t.codice}`} />
                    </div>
                  )}

                  {scope !== "fornitore" && (
                    <div>
                      <div className="flex justify-between items-center mb-1.5">
                        <label className="text-xs font-semibold uppercase tracking-wider text-amber-600 font-mono">
                          % Majorbit (cliente)
                          <Hint id="perc-cliente" testo={AIUTI.percCliente} />
                        </label>
                        <div className="flex items-center gap-2">
                          {disallineato && editMj && (
                            <Button variant="ghost" size="sm" onClick={() => allinea(t)}
                                    className="h-6 px-2 text-[11px] text-indigo-600 hover:bg-indigo-50"
                                    data-testid={`task-allinea-${t.codice}`}>
                              <Wand2 className="h-3 w-3 mr-1" /> Allinea al fornitore
                            </Button>
                          )}
                          <span className="font-mono text-sm font-bold text-slate-900"
                                data-testid={`task-cliente-value-${t.codice}`}>
                            {t.percentuale_cliente}%
                          </span>
                        </div>
                      </div>
                      <Slider value={[t.percentuale_cliente]} min={0} max={100} step={5} disabled={!editMj}
                              onValueChange={([v]) =>
                                setPlan((p) => ({
                                  ...p,
                                  tasks: p.tasks.map((x) => (x.id === t.id ? { ...x, percentuale_cliente: v } : x)),
                                }))
                              }
                              onValueCommit={([v]) => save(t, { percentuale_cliente: v })}
                              data-testid={`task-cliente-slider-${t.codice}`} />
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Select value={t.stato} disabled={!editCommon} onValueChange={(v) => save(t, { stato: v })}>
                      <SelectTrigger className="h-9 flex-1" data-testid={`task-status-select-${t.codice}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {statiSelezionabili(t).map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    <div className="flex items-center border border-slate-200 rounded-md h-9">
                      <Button variant="ghost" size="sm" disabled={role !== "admin" || t.rework_count === 0}
                              className="h-9 px-2 hover:bg-slate-100"
                              onClick={() => save(t, { rework_count: Math.max(0, t.rework_count - 1) })}
                              data-testid={`task-rework-minus-${t.codice}`}>
                        <Minus className="h-3.5 w-3.5" />
                      </Button>
                      <span className="px-2 font-mono text-sm w-16 text-center text-slate-700"
                            data-testid={`task-rework-value-${t.codice}`}>
                        RW {t.rework_count}
                      </span>
                      <Button variant="ghost" size="sm" disabled={role !== "admin"} className="h-9 px-2 hover:bg-slate-100"
                              onClick={() => save(t, { rework_count: t.rework_count + 1 })}
                              data-testid={`task-rework-plus-${t.codice}`}>
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  {(role === "admin" || role === "dev") && !readOnly && !attivitaDiTest(t) && (
                    <Select value={t.assegnatario || "-"} onValueChange={(v) => save(t, { assegnatario: v })}>
                      <SelectTrigger className="h-9" data-testid={`task-assignee-select-${t.codice}`}>
                        <SelectValue placeholder="Assegnatario" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {plan.assegnatari.map((a) => (<SelectItem key={a} value={a}>{a}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                <div className="lg:col-span-3 space-y-3">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono">
                      Note esecutore
                      <Hint id="note-esecutore" testo={AIUTI.noteEsecutore} />
                    </label>
                    <Textarea value={t.note || ""} disabled={!editCommon} rows={3}
                              placeholder={t.stato === "KO" ? "Sintesi del problema..." : "Note..."}
                              className="mt-1.5 text-sm resize-none"
                              onChange={(e) =>
                                setPlan((p) => ({
                                  ...p,
                                  tasks: p.tasks.map((x) => (x.id === t.id ? { ...x, note: e.target.value } : x)),
                                }))
                              }
                              onBlur={(e) => editCommon && save(t, { note: e.target.value })}
                              data-testid={`task-note-input-${t.codice}`} />
                  </div>
                  {scope !== "fornitore" && (
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-amber-600 font-mono">
                        Note Majorbit
                        <Hint id="note-cliente" testo={AIUTI.noteCliente} />
                      </label>
                      <Textarea value={t.note_cliente || ""} disabled={!editMj} rows={2}
                                placeholder="Nota di governance..."
                                className="mt-1.5 text-sm resize-none"
                                onChange={(e) =>
                                  setPlan((p) => ({
                                    ...p,
                                    tasks: p.tasks.map((x) => (x.id === t.id ? { ...x, note_cliente: e.target.value } : x)),
                                  }))
                                }
                                onBlur={(e) => editMj && save(t, { note_cliente: e.target.value })}
                                data-testid={`task-note-cliente-input-${t.codice}`} />
                    </div>
                  )}
                  {saving === t.id && (
                    <p className="text-[11px] text-slate-400 flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" /> Salvataggio...
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {scopedTasks.length === 0 && (
          <p className="text-center text-sm text-slate-400 py-12 bg-white border border-slate-200 rounded-xl">
            Nessun task corrisponde ai filtri selezionati.
          </p>
        )}
      </div>

      <PmBot pagina={scope === "all" ? "piano" : scope} sprint={sprint || null} />

    </div>
  );
}
